import Phaser from 'phaser';
import { tr } from '../i18n';
import { DEPTH, UI } from './theme';
import { FocusNav, FocusItem } from './FocusNav';
import {
  rollTalents,
  getTalent,
  talentName,
  talentDesc,
  talentsSummary,
  TalentDef,
  getTalentSlots,
  TALENT_MAX,
} from '../talents';
import type Player from '../objects/Player';

/** 天赋面板需要宿主（GameScene）提供的能力 */
export interface TalentHost {
  getPlayer(): Player;
  getFloor(): number;
  pausePhysics(): void;
  resumePhysics(): void;
  /** 宿主是否处于暂停菜单（若在暂停中关闭天赋面板，不应恢复物理） */
  isGamePaused(): boolean;
  /** 天赋变化后刷新 HUD */
  onTalentsChanged(): void;
}

const OFFER_COUNT = 3;
const TITLE_Y = 52;
const SUMMARY_Y = 92;
const CARD_Y0 = 185;
const CARD_GAP = 100;
const EXTRA_Y = 462;

/**
 * 天赋选择面板（进门时触发）
 * - 选择界面：3 个天赋 + 「跳过」
 * - 已满 3 个且选新天赋 → 放弃界面：3 个「放弃」+ 「返回」
 */
export default class TalentPanel {
  private open_ = false;
  private replacing = false;
  private offers: TalentDef[] = [];
  private pending?: TalentDef;
  private ui: Phaser.GameObjects.GameObject[] = [];
  private actions: (() => void)[] = [];
  private nav?: FocusNav;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly host: TalentHost
  ) {}

  get isOpen() {
    return this.open_;
  }

  // ── 打开 / 关闭 ───────────────────────────────────────────

  open() {
    if (this.open_) return;
    this.open_ = true;
    this.replacing = false;
    this.pending = undefined;
    this.host.pausePhysics();
    this.offers = rollTalents(
      OFFER_COUNT,
      (id) => (this.host.getPlayer().talents.get(id) ?? 0) >= TALENT_MAX
    );
    this.render();
    this.scene.input.keyboard!.on('keydown', this.onKey);
  }

  close() {
    this.open_ = false;
    this.replacing = false;
    this.pending = undefined;
    this.actions = [];
    this.scene.input.keyboard!.off('keydown', this.onKey);
    this.nav?.destroy();
    this.nav = undefined;
    this.clearUi();
    this.offers = [];
    if (!this.host.isGamePaused()) this.host.resumePhysics();
    this.host.onTalentsChanged();
  }

  /** ESC / 返回：放弃界面 → 回到选择界面；选择界面 → 跳过 */
  escape() {
    if (!this.open_) return;
    if (this.replacing) this.back();
    else this.close();
  }

  /** 从「放弃」返回「选择」 */
  back() {
    if (!this.open_ || !this.replacing) return;
    this.replacing = false;
    this.pending = undefined;
    this.render();
  }

  // ── 键盘导航（供 GameScene 的箭头键路由）─────────────────

  move(dx: number, dy: number) {
    this.nav?.move(dx, dy);
  }
  select() {
    this.nav?.select();
  }

  // ── 内部 ──────────────────────────────────────────────────

  private onKey = (event: KeyboardEvent) => {
    if (!this.open_) return;
    const n = Number(event.key);
    if (!Number.isInteger(n) || n < 1) return;
    this.actions[n - 1]?.();
  };

  private render() {
    this.clearUi();
    this.nav?.destroy();
    this.nav = undefined;

    const w = this.scene.scale.width;
    const h = this.scene.scale.height;
    const player = this.host.getPlayer();

    this.ui.push(
      this.scene.add
        .rectangle(0, 0, w, h, 0x000000, 0.78)
        .setOrigin(0)
        .setScrollFactor(0)
        .setDepth(DEPTH.talentOverlay)
    );

    const title = this.replacing
      ? tr('talent.titleReplace')
      : tr('talent.titleChoose', { floor: this.host.getFloor() });
    this.ui.push(
      this.scene.add
        .text(w / 2, TITLE_Y, title, {
          fontSize: '26px',
          fontFamily: 'monospace',
          color: this.replacing ? UI.red : UI.gold,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTH.talentContent)
    );
    this.ui.push(
      this.scene.add
        .text(w / 2, SUMMARY_Y, talentsSummary(player.talents, this.host.getFloor()), {
          fontSize: '15px',
          fontFamily: 'monospace',
          color: UI.muted,
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTH.talentContent)
    );

    const cards: Phaser.GameObjects.Text[] = [];
    const actions: (() => void)[] = [];
    const hover: [string, string][] = [];

    if (!this.replacing) {
      this.offers.forEach((t, i) => {
        const cur = player.talents.get(t.id) ?? 0;
        const next = Math.min(TALENT_MAX, cur + t.step);
        const nm = talentName(t);
        const head =
          cur > 0
            ? tr('talent.cardUp', { name: nm, cur, next })
            : tr('talent.cardNew', { name: nm, next });
        const card = this.scene.add
          .text(w / 2, CARD_Y0 + i * CARD_GAP, `[${i + 1}]  ${head}\n${talentDesc(t, next)}`, {
            fontSize: '20px',
            fontFamily: 'monospace',
            color: UI.text,
            align: 'center',
            backgroundColor: '#1e2740',
            padding: { x: 26, y: 12 },
          })
          .setOrigin(0.5)
          .setScrollFactor(0)
          .setDepth(DEPTH.talentContent)
          .setInteractive({ useHandCursor: true });
        card.on('pointerover', () => card.setColor(UI.gold));
        card.on('pointerout', () => card.setColor(UI.text));
        card.on('pointerdown', () => this.choose(i));
        this.ui.push(card);
        cards.push(card);
        actions.push(() => this.choose(i));
        hover.push([UI.gold, UI.text]);
      });
    } else {
      const held = [...player.talents.entries()];
      held.forEach(([id, v], i) => {
        const t = getTalent(id);
        const card = this.scene.add
          .text(
            w / 2,
            CARD_Y0 + i * CARD_GAP,
            tr('talent.dropCard', { i: i + 1, name: t ? talentName(t) : id, v }),
            {
              fontSize: '20px',
              fontFamily: 'monospace',
              color: UI.text,
              align: 'center',
              backgroundColor: '#3a1e24',
              padding: { x: 26, y: 14 },
            }
          )
          .setOrigin(0.5)
          .setScrollFactor(0)
          .setDepth(DEPTH.talentContent)
          .setInteractive({ useHandCursor: true });
        card.on('pointerover', () => card.setColor(UI.danger));
        card.on('pointerout', () => card.setColor(UI.text));
        card.on('pointerdown', () => this.replace(i));
        this.ui.push(card);
        cards.push(card);
        actions.push(() => this.replace(i));
        hover.push([UI.danger, UI.text]);
      });
    }

    // 第 4 项：跳过 / 返回
    const extraLabel = this.replacing ? tr('talent.back') : tr('talent.skip');
    const extraColor = this.replacing ? UI.doorUp : UI.muted;
    const extraAction = this.replacing ? () => this.back() : () => this.close();
    const extra = this.scene.add
      .text(w / 2, EXTRA_Y, extraLabel, {
        fontSize: '18px',
        fontFamily: 'monospace',
        color: extraColor,
        backgroundColor: '#20242f',
        padding: { x: 18, y: 8 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH.talentContent)
      .setInteractive({ useHandCursor: true });
    extra.on('pointerover', () => extra.setColor(UI.gold));
    extra.on('pointerout', () => extra.setColor(extraColor));
    extra.on('pointerdown', () => extraAction());
    this.ui.push(extra);
    cards.push(extra);
    actions.push(extraAction);
    hover.push([UI.gold, extraColor]);

    this.ui.push(this.hint(tr('talent.hintChoose')));
    this.actions = actions;

    this.nav = new FocusNav(this.scene, 0, DEPTH.talentNav);
    const items: FocusItem[] = cards.map((c, i) => ({
      row: i,
      col: 0,
      target: c,
      onSelect: actions[i],
      onFocus: () => c.setColor(hover[i][0]),
      onBlur: () => c.setColor(hover[i][1]),
    }));
    this.nav.setItems(items, 0);
  }

  private hint(s: string): Phaser.GameObjects.Text {
    return this.scene.add
      .text(this.scene.scale.width / 2, this.scene.scale.height - 40, s, {
        fontSize: '16px',
        fontFamily: 'monospace',
        color: UI.gold,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH.talentContent);
  }

  private choose(index: number) {
    if (!this.open_ || this.replacing) return;
    const t = this.offers[index];
    if (!t) return;
    const player = this.host.getPlayer();
    const cur = player.talents.get(t.id) ?? 0;

    if (cur > 0) {
      player.addTalent(t.id, cur + t.step);
      this.close();
    } else {
      const slots = getTalentSlots(this.host.getFloor());
      if (player.talents.size < slots) {
        player.addTalent(t.id, t.step);
        this.close();
      } else {
        this.pending = t;
        this.replacing = true;
        this.render();
      }
    }
  }

  private replace(index: number) {
    if (!this.open_ || !this.replacing || !this.pending) return;
    const player = this.host.getPlayer();
    const dropId = [...player.talents.keys()][index];
    if (!dropId) return;
    player.dropTalent(dropId);
    player.addTalent(this.pending.id, this.pending.step);
    this.pending = undefined;
    this.close();
  }

  private clearUi() {
    this.ui.forEach((o) => o.destroy());
    this.ui = [];
  }
}
